import { useState, useEffect } from 'react';
import Calendar from './components/Calendar';
import AnnouncementModal from './components/AnnouncementModal';
import DataMergeModal from './components/DataMergeModal';
import HelpModal from './components/HelpModal';
import { DialogProvider } from './contexts/DialogContext';
import { AuthProvider } from './contexts/AuthContext';
import { CalendarDataProvider } from './contexts/CalendarDataContext';
import { SelectionProvider, useSelectionContext } from './contexts/SelectionContext';
import { SidebarProvider, useSidebarContext } from './contexts/SidebarContext';
import { DragProvider } from './contexts/DragContext';
import { ContextMenuProvider } from './contexts/ContextMenuContext';
import { LanguageProvider } from './i18n';
import ErrorBoundary from './components/ErrorBoundary';
import ErrorFallback from './components/ErrorFallback';
import { useFirstLoginMerge } from './hooks/useFirstLoginMerge';
import { useAuth } from './hooks/useAuth';
import useDialog from './hooks/useDialog';
import { useTranslation } from './i18n/useTranslation';
import { clearData, hasExistingData } from './utils/storage';
import { getLastSeenVersion, setLastSeenVersion } from './utils/versionStorage';
import { currentVersion, changelog, getVersionsSince } from './changelog';
import { SIDEBAR_TYPE, SIDEBAR_MODE } from './constants/types';
import './App.scss';

function App() {
  const currentYear = new Date().getFullYear();

  const handleResetData = () => {
    clearData();
    window.location.reload();
  };

  return (
    <LanguageProvider>
      <ErrorBoundary
        fallback={({ error, errorInfo, reset }) => (
          <ErrorFallback
            error={error}
            errorInfo={errorInfo}
            reset={reset}
            onResetData={handleResetData}
          />
        )}
      >
        <DialogProvider>
          <AuthProvider>
            <CalendarDataProvider>
              <CalendarUIProviders>
                <AppContent currentYear={currentYear} />
              </CalendarUIProviders>
            </CalendarDataProvider>
          </AuthProvider>
        </DialogProvider>
      </ErrorBoundary>
    </LanguageProvider>
  );
}

/**
 * AppContent component - contains main app UI and handles announcement/merge modals
 * Now reactive to auth state
 */
function AppContent({ currentYear }) {
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  const { alert } = useDialog();
  const { t } = useTranslation();
  const [announcementState, setAnnouncementState] = useState({
    isOpen: false,
    mode: null,
    versions: []
  });
  const [showHelpModal, setShowHelpModal] = useState(false);

  // First-login merge flow (existing)
  const {
    showMergeModal,
    localData,
    handleMerge,
    handleDiscard,
    handleClose,
  } = useFirstLoginMerge();

  // Handle auth errors from backend redirects
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authError = params.get('auth_error');
    if (authError) {
      // Clean URL immediately
      window.history.replaceState({}, '', '/');
      // Show error dialog
      alert({
        title: t('auth.loginErrorTitle'),
        message: t('auth.loginErrorMessage', { reason: authError }),
        variant: 'error',
      });
    }
  }, [alert, t]);

  // Compute announcement state reactively based on auth state
  // This is intentionally setting state in an effect to react to auth changes
  useEffect(() => {
    if (authLoading) return; // Wait for auth check to complete

    const lastSeen = getLastSeenVersion();
    const hasData = hasExistingData();

    let newState = { isOpen: false, mode: null, versions: [] };

    if (isAuthenticated) {
      // Authenticated user: only show changelog for new versions
      if (lastSeen && lastSeen !== currentVersion) {
        const newVersions = getVersionsSince(lastSeen);
        if (newVersions.length > 0) {
          newState = {
            isOpen: true,
            mode: 'changelog',
            versions: newVersions
          };
        }
      }
    } else {
      // Anonymous user: show welcome-choice modal
      // Show it if they've never seen a version OR if they have local data (reminder)
      if (!lastSeen || hasData) {
        newState = {
          isOpen: true,
          mode: 'welcome-choice',
          versions: []
        };
      }
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAnnouncementState(newState);
  }, [isAuthenticated, authLoading]);

  const handleLogin = async () => {
    const result = await login();
    if (result && !result.success) {
      // Show error dialog if login failed (backend offline)
      await alert({
        title: t('auth.loginErrorTitle'),
        message: t('auth.serverOffline'),
        variant: 'error',
      });
    }
  };

  const handleUseLocally = () => {
    setLastSeenVersion(currentVersion);
    setAnnouncementState({ isOpen: false, mode: null, versions: [] });
  };

  const handleDismissAnnouncement = () => {
    // Update last seen version when dismissing welcome or changelog
    if (announcementState.mode === 'welcome' || announcementState.mode === 'changelog') {
      setLastSeenVersion(currentVersion);
    }

    setAnnouncementState({ isOpen: false, mode: null, versions: [] });
  };

  const handleShowInfo = () => {
    setAnnouncementState({
      isOpen: true,
      mode: 'info',
      versions: changelog
    });
  };

  // Handle '?' key to show help modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Show help modal when '?' is pressed (Shift + /)
      if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // Don't trigger if typing in an input field
        const isInputFocused = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName);
        if (!isInputFocused) {
          e.preventDefault();
          setShowHelpModal(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="app">
      <Calendar 
        initialYear={currentYear}
        onShowInfo={handleShowInfo}
      />
      <AnnouncementModal
        isOpen={announcementState.isOpen}
        mode={announcementState.mode}
        versions={announcementState.versions}
        onDismiss={handleDismissAnnouncement}
        onLogin={handleLogin}
        onUseLocally={handleUseLocally}
      />
      <DataMergeModal
        isOpen={showMergeModal}
        onClose={handleClose}
        localData={localData}
        onMerge={handleMerge}
        onDiscard={handleDiscard}
      />
      <HelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />
    </div>
  );
}

/**
 * Composition component that wires up all UI contexts with proper inter-context communication
 */
function CalendarUIProviders({ children }) {
  return (
    <SidebarProvider>
      <SelectionWithSidebar>
        <DragWithSelection>
          <ContextMenuWithSidebarAndSelection>
            {children}
          </ContextMenuWithSidebarAndSelection>
        </DragWithSelection>
      </SelectionWithSidebar>
    </SidebarProvider>
  );
}

/**
 * SelectionProvider wired to open sidebar on multi-day selection complete
 */
function SelectionWithSidebar({ children }) {
  const { openSidebar } = useSidebarContext();
  
  return (
    <SelectionProvider
      onSelectionComplete={(start, end) => {
        openSidebar(SIDEBAR_TYPE.EVENT, SIDEBAR_MODE.CREATE, { startDate: start, endDate: end });
      }}
    >
      {children}
    </SelectionProvider>
  );
}

/**
 * DragProvider wired to clear selection on drag start
 */
function DragWithSelection({ children }) {
  const { clearSelection } = useSelectionContext();
  
  return (
    <DragProvider onClearSelection={clearSelection}>
      {children}
    </DragProvider>
  );
}

/**
 * ContextMenuProvider wired to sidebar and selection
 */
function ContextMenuWithSidebarAndSelection({ children }) {
  const { openSidebar } = useSidebarContext();
  const { selectionStart, selectionEnd } = useSelectionContext();
  
  return (
    <ContextMenuProvider 
      openSidebar={openSidebar}
      selectionStart={selectionStart}
      selectionEnd={selectionEnd}
    >
      {children}
    </ContextMenuProvider>
  );
}

export default App;
